import React from 'react';
import { Card, Typography, Radio, Checkbox, Input, Upload, Button, Space, Divider, Tag } from 'antd';
import { UploadOutlined, UserOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Survey, Question } from '../services/surveyService';
import { getAnsweredByLabel } from '../utils/surveyUtils';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface SurveyPreviewProps {
  survey: Survey;
}

const SurveyPreview: React.FC<SurveyPreviewProps> = ({ survey }) => {
  
  // 渲染单个问题
  const renderQuestion = (question: Question, index: number) => {
    const questionNumber = index + 1;
    const answerLabel = getAnsweredByLabel(question);
    
    return (
      <Card key={question.id || index} style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Text strong>
            {questionNumber}. {question.title}
            {question.required && <span style={{ color: 'red' }}> *</span>}
          </Text>
          <Tag 
            color={answerLabel.color} 
            icon={question.answered_by === 'user' ? <UserOutlined /> : <CheckCircleOutlined />}
          >
            {answerLabel.text}
          </Tag>
        </div>
        
        {renderQuestionInput(question)}
      </Card>
    );
  };

  // 根据问题类型渲染输入组件
  const renderQuestionInput = (question: Question) => {
    switch (question.type) {
      case 'text':
        return (
          <Input 
            placeholder="请输入文本" 
            disabled 
            style={{ width: '50%' }}
          />
        );
        
      case 'textarea':
        return (
          <TextArea 
            rows={4} 
            placeholder="请输入文本内容" 
            disabled 
            style={{ width: '80%' }}
          />
        );
        
      case 'radio':
        return (
          <Radio.Group disabled>
            <Space direction="vertical">
              {question.options?.map((option, optionIndex) => (
                <Radio key={optionIndex} value={option.value}>
                  {option.label}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        );
        
      case 'checkbox':
        return (
          <Checkbox.Group disabled>
            <Space direction="vertical">
              {question.options?.map((option, optionIndex) => (
                <Checkbox key={optionIndex} value={option.value}>
                  {option.label}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        );
        
      case 'file':
        return (
          <div>
            <Upload disabled>
              <Button icon={<UploadOutlined />} disabled>
                选择文件
              </Button>
            </Upload>
            <div style={{ marginTop: 8, color: '#999', fontSize: '12px' }}>
              {question.file_types && (
                <div>支持的文件类型: {question.file_types.join(', ')}</div>
              )}
              {question.max_size && (
                <div>最大文件大小: {question.max_size}MB</div>
              )}
            </div>
          </div>
        );
        
      case 'sort':
        return (
          <div>
            <div style={{ color: '#666', marginBottom: 8 }}>请拖拽下方选项进行排序：</div>
            <div style={{ border: '1px dashed #d9d9d9', borderRadius: 4, padding: 12 }}>
              {question.options?.map((option, optionIndex) => (
                <div 
                  key={optionIndex} 
                  style={{ 
                    padding: '8px 12px',
                    margin: '4px 0',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    cursor: 'move',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <span style={{ marginRight: 8, color: '#999' }}>⋮⋮</span>
                  {option.label}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8, color: '#999', fontSize: '12px' }}>
              预览模式，无法实际排序
            </div>
          </div>
        );
        
      default:
        return (
          <Input 
            placeholder="未知问题类型" 
            disabled 
            style={{ width: '50%' }}
          />
        );
    }
  };

  // 按分组整理问题
  const groupQuestionsBySection = (questions: Question[]) => {
    const sectionMap: { [key: string]: Question[] } = {};
    
    questions.forEach(question => {
      const section = question.section || '默认分组';
      if (!sectionMap[section]) {
        sectionMap[section] = [];
      }
      sectionMap[section].push(question);
    });

    return Object.entries(sectionMap).map(([sectionName, questions]) => ({
      name: sectionName,
      questions: questions.sort((a, b) => (a.order_num || 0) - (b.order_num || 0))
    }));
  };

  const sections = groupQuestionsBySection(survey.questions || []);

  return (
    <div style={{ padding: 24, maxHeight: '70vh', overflow: 'auto' }}>
      {/* 问卷头部信息 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>{survey.title}</Title>
        {survey.description && (
          <Text type="secondary" style={{ fontSize: '14px', lineHeight: '1.6' }}>
            {survey.description}
          </Text>
        )}
        
                 <div style={{ marginTop: 16 }}>
           <Space split={<Divider type="vertical" />}>
             <Text type="secondary">类型: {survey.type}</Text>
             {survey.price && <Text type="secondary">劳务费: ¥{survey.price}</Text>}
           </Space>
         </div>
      </Card>

      {/* 问题列表 */}
      {sections.map((section, sectionIndex) => (
        <div key={sectionIndex} style={{ marginBottom: 32 }}>
          {sections.length > 1 && (
            <Title level={4} style={{ 
              marginBottom: 16, 
              paddingBottom: 8, 
              borderBottom: '1px solid #f0f0f0' 
            }}>
              {section.name}
            </Title>
          )}
          
          {section.questions.map((question, questionIndex) => {
            // 计算全局问题编号
            let globalIndex = 0;
            for (let i = 0; i < sectionIndex; i++) {
              globalIndex += sections[i].questions.length;
            }
            globalIndex += questionIndex;
            
            return renderQuestion(question, globalIndex);
          })}
        </div>
      ))}

      {/* 提交按钮（预览状态） */}
      <div style={{ textAlign: 'center', marginTop: 32 }}>
        <Button type="primary" size="large" disabled>
          提交问卷（预览模式）
        </Button>
      </div>
    </div>
  );
};

export default SurveyPreview; 